import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const getStorageKey = (pathname, search) => `scroll:${pathname}${search || ""}`;

const readY = (key) => {
  const raw = sessionStorage.getItem(key);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const saveY = (key) => {
  sessionStorage.setItem(key, String(window.scrollY || window.pageYOffset || 0));
};

const restoreY = (y) => {
  window.scrollTo(0, y);

  // Retry once after paint in case async content changes the layout.
  requestAnimationFrame(() => {
    window.scrollTo(0, y);
  });

  setTimeout(() => {
    window.scrollTo(0, y);
  }, 60);
};

export default function ScrollPersistence() {
  const { pathname, search, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // If a hash anchor is targeted (e.g. #strength, #pricing), don't force-reset to top
    if (hash) {
      return;
    }

    const key = getStorageKey(pathname, search);
    const savedY = readY(key);

    // Restore only for browser history navigation (initial load/back/forward/reload).
    // For normal in-app route changes (PUSH/REPLACE), do not restore previous position.
    if (navigationType === "POP" && savedY !== null) {
      restoreY(savedY);
    } else if (navigationType !== "POP") {
      window.scrollTo(0, 0);
    }

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        saveY(key);
        ticking = false;
      });
    };

    const onBeforeUnload = () => {
      saveY(key);
    };

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      saveY(key);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [pathname, search, navigationType]);

  return null;
}